import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Heart, Check, Users, Stethoscope, Pill, Shield, Baby, Eye, CircleDot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

// Medical Aid Plans with full pricing and benefits
const medicalPlans = [
  {
    id: 'essential',
    name: 'Essential Plan',
    monthly: 850,
    icon: Pill,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    hospital: 'State hospitals only',
    gp_visits: '2 per year',
    specialists: 'Limited referral',
    benefits: [
      'Basic GP consultations',
      'Emergency room cover',
      'Chronic medication (limited)',
      'Basic dental check-up',
    ],
    savings: 0,
    recommended: false
  },
  {
    id: 'classic',
    name: 'Classic Plan',
    monthly: 1650,
    icon: Stethoscope,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    hospital: 'Private & State',
    gp_visits: '6 per year',
    specialists: '4 per year',
    benefits: [
      'Unlimited GP consultations',
      'Private hospital admission',
      'Full chronic medication',
      'Dental & optical benefits',
      'Maternity cover',
    ],
    savings: 3000,
    recommended: false
  },
  {
    id: 'comprehensive',
    name: 'Comprehensive Plan',
    monthly: 2850,
    icon: Heart,
    color: 'text-afriperx-gold',
    bgColor: 'bg-red-100',
    hospital: 'Full private',
    gp_visits: 'Unlimited',
    specialists: 'Unlimited',
    benefits: [
      'Unlimited GP & specialist visits',
      'Premium private hospitals',
      'Full chronic & acute medication',
      'Comprehensive dental & optical',
      'Full maternity & newborn cover',
      'Mental health support',
      'International emergency cover',
    ],
    savings: 8000,
    recommended: true
  },
  {
    id: 'family',
    name: 'Family Plus Plan',
    monthly: 3950,
    icon: Users,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    hospital: 'Full private',
    gp_visits: 'Unlimited',
    specialists: 'Unlimited',
    benefits: [
      'All Comprehensive benefits',
      'Cover for spouse + 4 children',
      'Pediatric specialist access',
      'School accident cover',
      'Family wellness programs',
      'Home nursing care',
      'Priority appointments',
    ],
    savings: 15000,
    recommended: false
  }
];

export const MedicalSection = () => {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan.id);
    toast.success(`${plan.name} selected! Our team will contact you to complete enrollment.`);
  };

  return (
    <div className="space-y-6" data-testid="medical-section">
      <h1 className="font-syne text-2xl font-bold text-gray-900">Medical Aid</h1>

      {/* Member Benefits Banner */}
      <Card className="card-raoul p-4 border-afriperx-gold/20 bg-gradient-to-r from-red-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-afriperx-green/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-afriperx-gold" />
          </div>
          <div>
            <p className="text-gray-900 font-medium">AfriPerx Member Benefit</p>
            <p className="text-gray-500 text-sm">Get up to R15,000 annual savings on medical expenses</p>
          </div>
        </div>
      </Card>

      {/* Plans Comparison */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {medicalPlans.map((plan) => {
          const Icon = plan.icon;
          const isSelected = selectedPlan === plan.id;
          
          return (
            <Card 
              key={plan.id} 
              className={`card-raoul p-5 relative overflow-hidden transition-all ${
                plan.recommended ? 'border-afriperx-gold/30 ring-1 ring-afriperx-gold/20' : ''
              } ${isSelected ? 'border-afriperx-gold ring-2 ring-afriperx-gold/30' : ''}`}
            >
              {plan.recommended && (
                <div className="absolute top-3 right-3 bg-afriperx-green text-white text-xs px-2 py-1 rounded-full font-medium">
                  Best Value
                </div>
              )}
              
              <div className={`w-12 h-12 rounded-xl ${plan.bgColor} flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${plan.color}`} />
              </div>
              
              <h3 className="font-syne text-lg font-bold text-gray-900 mb-1">{plan.name}</h3>
              
              <div className="mb-4">
                <p className="text-3xl font-bold text-gray-900">
                  R{plan.monthly.toLocaleString()}
                  <span className="text-sm text-gray-500 font-normal">/month</span>
                </p>
                {plan.savings > 0 && (
                  <p className="text-green-600 text-sm font-medium">
                    Save up to R{plan.savings.toLocaleString()}/year
                  </p>
                )}
              </div>
              
              {/* Quick Stats */}
              <div className="space-y-2 mb-4 pb-4 border-b border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Hospital</span>
                  <span className="text-gray-900 font-medium">{plan.hospital}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">GP Visits</span>
                  <span className="text-gray-900 font-medium">{plan.gp_visits}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Specialists</span>
                  <span className="text-gray-900 font-medium">{plan.specialists}</span>
                </div>
              </div>

              {/* Benefits List */}
              <div className="space-y-2 mb-4">
                {plan.benefits.slice(0, 4).map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{benefit}</span>
                  </div>
                ))}
                {plan.benefits.length > 4 && (
                  <p className="text-gray-400 text-xs pl-6">+{plan.benefits.length - 4} more benefits</p>
                )}
              </div>

              <Button
                onClick={() => handleSelectPlan(plan)}
                className={`w-full ${plan.recommended ? 'btn-primary' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                data-testid={`medical-select-${plan.id}`}
              >
                {isSelected ? 'Selected' : 'Select Plan'}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Additional Coverage Options */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="card-raoul p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
            <CircleDot className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-gray-900 font-medium">Dental Add-on</p>
          <p className="text-gray-500 text-sm">From R250/month</p>
        </Card>
        <Card className="card-raoul p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <Eye className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-gray-900 font-medium">Optical Add-on</p>
          <p className="text-gray-500 text-sm">From R180/month</p>
        </Card>
        <Card className="card-raoul p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-3">
            <Baby className="w-5 h-5 text-pink-600" />
          </div>
          <p className="text-gray-900 font-medium">Maternity Add-on</p>
          <p className="text-gray-500 text-sm">From R450/month</p>
        </Card>
        <Card className="card-raoul p-4 text-center">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
            <Shield className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-gray-900 font-medium">Gap Cover</p>
          <p className="text-gray-500 text-sm">From R350/month</p>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="card-raoul p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-afriperx-green/10 flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 text-afriperx-gold" />
          </div>
          <div>
            <h3 className="font-syne text-lg font-bold text-gray-900 mb-2">Family Coverage</h3>
            <p className="text-gray-500 text-sm mb-3">
              Add dependents to any plan for additional cover. Our Family Plus plan offers the best value for families with up to 4 children included.
            </p>
            <div className="flex gap-4 text-sm">
              <div>
                <p className="text-gray-900 font-medium">Adult Dependent</p>
                <p className="text-gray-500">+R650/month</p>
              </div>
              <div>
                <p className="text-gray-900 font-medium">Child (under 21)</p>
                <p className="text-gray-500">+R350/month</p>
              </div>
              <div>
                <p className="text-gray-900 font-medium">Student (under 26)</p>
                <p className="text-gray-500">+R450/month</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
